import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInterval } from 'hooks/useInterval';
import { formatTimerDuration } from 'features/timer/utils/formatTimerDuration';
import { TimerHookState, TimerMode } from './types';

export interface TimerConfig {
  mode: TimerMode;
  totalSeconds?: number; // For countdown mode
  autoStart?: boolean;
  allowNegative?: boolean;
  onComplete?: () => void;
  onTick?: (currentSeconds: number) => void;
  onStateChange?: (state: TimerHookState) => void;
}

export interface TimerControls {
  start: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  reset: () => void;
  setTime: (seconds: number) => void;
}

export interface TimerData {
  currentSeconds: number;
  totalSeconds: number;
  state: TimerHookState;
  mode: TimerMode;
  isRunning: boolean;
  isPaused: boolean;
  isCompleted: boolean;
  formattedTime: string; // HH:MM:SS format
}

export const useTimer = (config: TimerConfig): [TimerData, TimerControls] => {
  const [state, setState] = useState<TimerHookState>('idle');
  const [currentSeconds, setCurrentSeconds] = useState(
    config.mode === 'countdown' ? config.totalSeconds || 0 : 0
  );
  const [totalSeconds, setTotalSeconds] = useState(config.totalSeconds || 0);

  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    const newTotalSeconds = config.totalSeconds || 0;
    if (newTotalSeconds !== totalSeconds) {
      setTotalSeconds(newTotalSeconds);
      setCurrentSeconds(config.mode === 'countdown' ? newTotalSeconds : 0);
      // Reset state when config changes significantly
      if (newTotalSeconds === 0) {
        setState('idle');
      }
    }
  }, [config.totalSeconds, config.mode, totalSeconds]);

  const tick = useCallback(() => {
    setCurrentSeconds((prev) => {
      const newValue = config.mode === 'countdown' ? prev - 1 : prev + 1;

      if (config.mode === 'countdown' && newValue <= 0) {
        if (!config.allowNegative) {
          setState('completed');
          return 0;
        }
        if (newValue === 0) {
          configRef.current.onComplete?.();
        }
      }

      configRef.current.onTick?.(newValue);
      return newValue;
    });
  }, [config.mode, config.allowNegative]);

  useInterval(tick, state === 'running' ? 1000 : null);

  // Fire onComplete outside the state updater to avoid StrictMode double-invocation.
  // StrictMode calls state updater functions twice with the same input to detect impurities,
  // which caused onComplete to dispatch duplicate submit requests.
  useEffect(() => {
    if (state === 'completed') {
      configRef.current.onComplete?.();
    }
    configRef.current.onStateChange?.(state);
  }, [state]);

  useEffect(() => {
    // Only auto-start if we have a valid totalSeconds > 0
    if (config.autoStart && state === 'idle' && totalSeconds > 0) {
      setState('running');
    }
  }, [config.autoStart, state, totalSeconds]);

  const start = useCallback(() => {
    // Only start if we have valid totalSeconds
    if (totalSeconds > 0) {
      setState('running');
    }
  }, [totalSeconds]);

  const pause = useCallback(() => {
    if (state === 'running') {
      setState('paused');
    }
  }, [state]);

  const resume = useCallback(() => {
    if (state === 'paused') {
      setState('running');
    }
  }, [state]);

  const stop = useCallback(() => {
    setState('idle');
    setCurrentSeconds(config.mode === 'countdown' ? totalSeconds : 0);
  }, [config.mode, totalSeconds]);

  const reset = useCallback(() => {
    setCurrentSeconds(config.mode === 'countdown' ? totalSeconds : 0);
    setState('idle');
  }, [config.mode, totalSeconds]);

  const setTime = useCallback((seconds: number) => {
    setCurrentSeconds(seconds);
  }, []);

  const controls = useMemo<TimerControls>(
    () => ({
      start,
      pause,
      resume,
      stop,
      reset,
      setTime,
    }),
    [start, pause, resume, stop, reset, setTime]
  );

  const timerData = {
    currentSeconds,
    totalSeconds,
    state,
    mode: config.mode,
    isRunning: state === 'running',
    isPaused: state === 'paused',
    isCompleted: state === 'completed',
    formattedTime: formatTimerDuration(currentSeconds),
  };

  return [timerData, controls];
};
